<template>
  <div class="flex justify-between items-center p-4 h-full">
    <div class="flex items-center gap-1 cursor-pointer">
      <Button
        @click="$emit('toggle-sidebar')"
        variant="ghost"
        size="icon"
        data-testid="navbar_menu_button"
        :aria-label="isMenuOpen ? 'Close side menu' : 'Open side menu'"
        :title="isMenuOpen ? 'Close side menu' : 'Open side menu'"
      >
        <Menu class="h-4 w-4" />
      </Button>
      <img src="@/assets/logo.svg" class="w-24 md:w-36" />
    </div>
    <div class="flex items-center gap-1 md:gap-4">
      <DropdownMenu v-if="canCreateOrder">
        <DropdownMenuTrigger>
          <div class="flex items-center gap-1">
            <Button
              type="button"
              @click="
                boostEnabled
                  ? (showDropdown = !showDropdown) // open / close dropdown
                  : $emit('show-create-order-modal')
              "
            >
              <Plus class="h-4 w-4" />
              Create Order
            </Button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" :sideOffset="10" v-if="boostEnabled">
          <DropdownMenuItem @click="$emit('show-create-order-modal')">
            <div class="flex items-center gap-2 cursor-pointer">
              <CreditCard class="w-4" />
              Payment Plan
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem v-if="boostEnabled" @click="$emit('show-create-boost-rewards-modal')">
            <div class="flex items-center gap-2 cursor-pointer">
              <PiggyBank class="w-4" />
              Boost Rewards
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="ghost" size="icon" @click="goToHelpCenter">
              <CircleHelp class="text-lg text-grey-light-500" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Frequently Asked Questions</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenu>
        <DropdownMenuTrigger>
          <div class="flex items-center gap-1">
            <div class="flex justify-center items-center w-10 h-10 rounded-full bg-brand-700 text-white">
              {{ initials }}
            </div>
            <ChevronDown class="w-4 h-4 text-gray-700" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" :sideOffset="10">
          <DropdownMenuLabel>
            <div class="flex gap-2 items-center">
              <div class="relative">
                <div class="flex justify-center items-center w-10 h-10 rounded-full bg-brand-700 text-white">
                  {{ initials }}
                </div>
                <div
                  class="h-[10px] w-[10px] rounded-full border border-white bg-success-500 absolute bottom-0 right-0"
                ></div>
              </div>
              <div>
                <h6 class="font-semibold text-sm text-gray-700">
                  {{ userStore.user?.name || '' }}
                </h6>
                <p class="text-sm text-gray-500">
                  {{ userStore.user?.email || '' }}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="$emit('show-profile', true)" class="cursor-pointer">
            <div class="flex items-center gap-2">
              <CircleUserRound class="w-4 h-4" />
              Profile
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem @click="router.push({ name: 'merchantSettings' })" class="cursor-pointer">
            <div class="flex items-center gap-2">
              <Settings class="w-4 h-4" />
              Settings
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem @click="onSignOut" class="cursor-pointer">
            <div class="flex items-center gap-2">
              <LogOut class="w-4 h-4" />
              Log out
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { getInitials } from '@/utils/text.utils';
import {
  ChevronDown,
  CircleHelp,
  CircleUserRound,
  CreditCard,
  LogOut,
  Menu,
  PiggyBank,
  Plus,
  Settings,
} from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

declare global {
  interface Window {
    fcWidget: {
      isOpen: () => boolean;
      open: () => void;
      show(): void;
      hide(): void;
    };
  }
}

const router = useRouter();
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const initials = ref('');

const showDropdown = ref(false);
const boostEnabled = computed(() => {
  return merchantStore.merchant?.voucherSettings?.active || false;
});
const canCreateOrder = computed(() => {
  return userStore.user.verifiedEmail && userStore.user.merchants?.length === 1;
});

onMounted(() => {
  const user = userStore.user;
  if (user && user.name) {
    initials.value = getInitials(user.name);
  } else {
    initials.value = 'LT';
  }
});

defineProps<{
  isMenuOpen: boolean;
}>();

defineEmits(['toggle-sidebar', 'show-profile', 'show-create-order-modal', 'show-create-boost-rewards-modal']);

const goToHelpCenter = () => {
  window.open('https://layup.co.za/faq/', '_blank');
};

const onSignOut = async () => {
  await userStore.signOut();
  await router.push({ name: 'merchantSignIn' });
};
</script>
